/**
 * @fileoverview Service for X/Twitter Connect API operations.
 * @module x-twitter-connect/services/XTwitterConnectService
 */

import { getAuth } from 'firebase/auth';
import type {
  XTwitterConnectionStatus,
  XTwitterDisconnectResponse,
  XTwitterOAuthUrlResponse,
  XTwitterSyncOwnPostsResponse,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }
  return 'https://api.segmently.ai';
}

export class XTwitterConnectService {
  private readonly apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || getApiUrl();
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getOAuthUrl(projectId: string, returnTo?: string): Promise<string> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    const response = await fetch(`${this.apiUrl}/api/x-twitter/oauth/url?${params}`, {
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get X OAuth URL' }));
      throw new Error(error.error || 'Failed to get X OAuth URL');
    }

    const data = await response.json() as XTwitterOAuthUrlResponse;
    return data.url;
  }

  async getStatus(projectId: string, signal?: AbortSignal): Promise<XTwitterConnectionStatus> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(`${this.apiUrl}/api/x-twitter/status?${params}`, {
      headers,
      signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get X connection status' }));
      throw new Error(error.error || 'Failed to get X connection status');
    }

    return response.json();
  }

  async disconnect(projectId: string): Promise<XTwitterDisconnectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/x-twitter/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to disconnect X account' }));
      throw new Error(error.error || 'Failed to disconnect X account');
    }

    return response.json();
  }

  async syncOwnPosts(request: {
    projectId: string;
    authorId?: string;
    maxResults?: number | 'all';
    maxPages?: number;
    limit?: number;
    maxCostUsd?: number;
    dryRun?: boolean;
  }): Promise<XTwitterSyncOwnPostsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/x-twitter/sync-own-posts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to sync X posts' }));
      throw new Error(error.error || 'Failed to sync X posts');
    }

    return response.json();
  }
}

let singleton: XTwitterConnectService | null = null;

export function getXTwitterConnectService(): XTwitterConnectService {
  if (!singleton) {
    singleton = new XTwitterConnectService();
  }
  return singleton;
}

export function createXTwitterConnectService(apiUrl?: string): XTwitterConnectService {
  return new XTwitterConnectService(apiUrl);
}

export default XTwitterConnectService;
